import React, {useEffect, useMemo, useRef, useState, useCallback} from "react";
import {ChatHistory} from "./types";
import {useSelector} from "react-redux";
import DeleteChatModal from "./DeleteChatModal";
import ChatDisplayRow from "./ChatDisplayRow";
import ChatEditInput from "./ChatEditInput";
import { timePresets } from "../../utils/timeUtils";
import Portal from "../Portal";

interface ChatHistoryItemProps {
  history: ChatHistory;
  onChatClick: (chat_id: number) => void;
  animateOnMount?: boolean;
}

const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({ history, onChatClick, animateOnMount = false }) => {
  const inputRefs = useRef({});
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const tooltipDelayRef = useRef<number | null>(null);
  const [showEditInput, setShowEditInput] = useState<Record<number, boolean>>({});
  const [showDeleteChatConfirmModal, setShowDeleteChatConfirmModal] = useState(false);
  const [mounted, setMounted] = useState(!animateOnMount);
  const [isExiting, setIsExiting] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [tooltipPlacement, setTooltipPlacement] = useState<'right' | 'left' | 'top' | 'bottom'>('right');
  const [tooltipReady, setTooltipReady] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isShared, setIsShared] = useState<boolean>((history as any)?.is_shared || false);

  const {
    lastActiveChatId
  } = useSelector((state?: any) => state.chatMessages);

  const resourcesData = useSelector((state: any) => state.resources.data);
  const resourceIdToNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (resourcesData || []).forEach((r: any) => {
      const id = r?.id != null ? String(r.id) : undefined;
      if (id) {
        map.set(id, r?.name || r?.email || 'Unknown');
      }
    });
    return map;
  }, [resourcesData]);
  const createdByName: string | undefined = useMemo(() => {
    const key = history?.created_by != null ? String(history.created_by) : undefined;
    return key ? resourceIdToNameMap.get(key) : undefined;
  }, [history?.created_by, resourceIdToNameMap]);

  const isEditing = showEditInput[history.chat_id];

  const handleDeleteClick = useCallback(() => {
    setShowDeleteChatConfirmModal(true);
  }, []);

  const handleToggleEditInput = useCallback((record: Record<number, boolean>) => {
    setShowEditInput(record)
  }, []);

  const handleButtonClick = useCallback(() => {
    if (!isEditing) onChatClick(history.chat_id);
  }, [isEditing, onChatClick, history.chat_id]);

  const scheduleShowTooltip = useCallback(() => {
    if (isMenuOpen) return;
    if (tooltipDelayRef.current) clearTimeout(tooltipDelayRef.current);
    tooltipDelayRef.current = window.setTimeout(() => {
      if (!isMenuOpen) setIsTooltipVisible(true);
    }, 300);
  }, [isMenuOpen]);

  const cancelAndHideTooltip = useCallback(() => {
    if (tooltipDelayRef.current) {
      clearTimeout(tooltipDelayRef.current);
      tooltipDelayRef.current = null;
    }
    setIsTooltipVisible(false);
    setTooltipReady(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    scheduleShowTooltip();
  }, [scheduleShowTooltip]);

  const handleMouseLeave = useCallback(() => {
    cancelAndHideTooltip();
  }, [cancelAndHideTooltip]);

  const handleFocus = useCallback(() => {
    scheduleShowTooltip();
  }, [scheduleShowTooltip]);

  const handleBlur = useCallback(() => {
    cancelAndHideTooltip();
  }, [cancelAndHideTooltip]);

  const handlePinned = useCallback(() => {
    setIsExiting(true);
  }, []);

  useEffect(() => {
    setIsShared((history as any)?.is_shared || false);
    if (animateOnMount) {
      const t = setTimeout(() => setMounted(true), 10);
      return () => clearTimeout(t);
    }
  }, [animateOnMount]);

  const historyItemBaseCls = "text-gray-700 dark:text-gray-300 text-xs sm:text-sm items-center flex gap-2 sm:gap-3 py-1 sm:py-1.5 px-2 sm:px-3 font-medium relative leading-normal group transition-colors duration-150";

  const computeAndSetTooltipPosition = useCallback(() => {
    const margin = 8;
    if (!buttonRef.current || !tooltipRef.current) return;
    const btn = buttonRef.current.getBoundingClientRect();
    const tip = tooltipRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let placement: 'right' | 'left' | 'top' | 'bottom' = 'right';
    let top = btn.top + btn.height / 2;
    let left = btn.right + margin;

    if (left + tip.width > vw - margin) {
      placement = 'left';
      left = btn.left - margin - tip.width;
      top = btn.top + btn.height / 2;
    }
    if (placement === 'left' && left < margin) {
      placement = 'top';
      top = btn.top - margin - tip.height;
      left = btn.left + btn.width / 2 - tip.width / 2;
    }
    if (placement === 'top' && top < margin) {
      placement = 'bottom';
      top = btn.bottom + margin;
      left = btn.left + btn.width / 2 - tip.width / 2;
    }

    top = Math.min(Math.max(margin, top), vh - tip.height - margin);
    left = Math.min(Math.max(margin, left), vw - tip.width - margin);

    setTooltipPlacement(placement);
    setTooltipPosition({ top, left });
  }, []);

  useEffect(() => {
    if (!isTooltipVisible) return;
    setTooltipReady(false);
    const id = requestAnimationFrame(() => {
      computeAndSetTooltipPosition();
      setTooltipReady(true);
    });
    const handleResize = () => computeAndSetTooltipPosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
      setTooltipReady(false);
    };
  }, [isTooltipVisible, computeAndSetTooltipPosition]);

  useEffect(() => {
    return () => {
      if (tooltipDelayRef.current) {
        clearTimeout(tooltipDelayRef.current);
        tooltipDelayRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <button
        ref={buttonRef}
        className={`${historyItemBaseCls} ${
          history.chat_id === lastActiveChatId
            ? "bg-gray-200 dark:bg-gray-700 text-left text-blue-600 dark:text-blue-400 rounded-md"
            : "hover:bg-gray-200 text-left dark:hover:bg-gray-800 rounded-md"
        } cursor-pointer transition-all duration-200 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'} ${isExiting ? 'opacity-0 -translate-y-1' : ''}`}
        key={history.chat_id}
        onClick={handleButtonClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {isEditing ? (
          <ChatEditInput
            history={history}
            inputRefs={inputRefs}
            showEditInput={showEditInput}
            onToggleEditInput={handleToggleEditInput}
          />
        ) : (
          <ChatDisplayRow
            history={history}
            inputRefs={inputRefs}
            onDeleteClick={handleDeleteClick}
            showEditInput={showEditInput}
            onToggleEditInput={handleToggleEditInput}
            onPinned={handlePinned}
            onMenuOpen={() => { setIsMenuOpen(true); cancelAndHideTooltip(); }}
            onMenuClose={() => setIsMenuOpen(false)}
            onShareChange={(shared) => setIsShared(!!shared)}
          />
        )}

      </button>

      {isTooltipVisible && !isMenuOpen && (
        <Portal>
          <div
            className="fixed z-[100000] pointer-events-none"
            style={{ 
              top: tooltipPosition.top, 
              left: tooltipPosition.left, 
              transform: tooltipPlacement === 'right' || tooltipPlacement === 'left' ? 'translateY(-50%)' : 'translateX(-50%)',
              visibility: tooltipReady ? 'visible' : 'hidden'
            }}
          >
            <div ref={tooltipRef} className="relative max-w-xs w-max whitespace-normal break-words text-xs rounded-md shadow-xl p-2 border bg-white text-gray-900 border-gray-200 dark:bg-gray-900 dark:text-white dark:border-gray-700">
              {tooltipPlacement === 'right' && (
                <>
                  <div className="absolute left-[-5px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 rotate-45 bg-gray-200 dark:bg-gray-700"></div>
                  <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 rotate-45 bg-white dark:bg-gray-900"></div>
                </>
              )}
              {tooltipPlacement === 'left' && (
                <>
                  <div className="absolute right-[-5px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 rotate-45 bg-gray-200 dark:bg-gray-700"></div>
                  <div className="absolute right-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 rotate-45 bg-white dark:bg-gray-900"></div>
                </>
              )}
              {tooltipPlacement === 'top' && (
                <>
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-[-5px] w-2.5 h-2.5 rotate-45 bg-gray-200 dark:bg-gray-700"></div>
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-[-4px] w-2 h-2 rotate-45 bg-white dark:bg-gray-900"></div>
                </>
              )}
              {tooltipPlacement === 'bottom' && (
                <>
                  <div className="absolute left-1/2 -translate-x-1/2 top-[-5px] w-2.5 h-2.5 rotate-45 bg-gray-200 dark:bg-gray-700"></div>
                  <div className="absolute left-1/2 -translate-x-1/2 top-[-4px] w-2 h-2 rotate-45 bg-white dark:bg-gray-900"></div>
                </>
              )}
              {isShared && (
                <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1">This chat is shared</div>
              )}
              <div className="font-semibold leading-snug">{history.title}</div>
              {createdByName && (
                <div className="opacity-80 leading-snug">Created by: {createdByName}</div>
              )}
              <div className="opacity-80 leading-snug">Created at: {timePresets.dateTime(history.created_at)}</div>
            </div>
          </div>
        </Portal>
      )}

      {showDeleteChatConfirmModal && (
        <DeleteChatModal
          onCloseModal={() => setShowDeleteChatConfirmModal(false)}
        />
      )}
    </>
  );
};

export default ChatHistoryItem;
